import { customBaseUrl, httpGet, httpPost, httpDelete } from '../store/http';

// ────────────────────────────────────────────────────────────────────────────
// Fraud Check Service
// Base URL: customBaseUrl.tranv2 (/transaction-payment)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Build a query string from params, ignoring null/undefined/empty values.
 */
const buildQuery = (params = {}) => {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== null && v !== undefined && v !== ''
  );
  if (!entries.length) return '';
  return '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
};

/**
 * GET /api/v1/admin/fraud/blocklist/counterparty
 * List blocklisted counterparty accounts
 */
export const getBlocklistedCounterparties = async () => {
  return await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/fraud/blocklist/counterparty`);
};

/**
 * POST /api/v1/admin/fraud/blocklist/counterparty
 * Add accounts to blocklist
 * Payload format: { accounts: ["accountNumber"], reason: "reason text" }
 */
export const addAccountsToBlocklist = async (payload) => {
  return await httpPost(`${customBaseUrl.tranv2}/api/v1/admin/fraud/blocklist/counterparty`, payload);
};

/**
 * DELETE /api/v1/admin/fraud/blocklist/counterparty/{account}
 * Remove a counterparty account from the blocklist
 */
export const removeAccountFromBlocklist = async (account) => {
  return await httpDelete(`${customBaseUrl.tranv2}/api/v1/admin/fraud/blocklist/counterparty/${account}`);
};

/**
 * GET /api/v1/admin/fraud/pending
 * List pending fraud actions awaiting checker decision
 */
export const getPendingFraudActions = async () => {
  return await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/fraud/pending`);
};

/**
 * GET /api/v1/admin/fraud/pending/{reference}
 * Fetch a single pending fraud action by reference
 */
export const getPendingFraudActionDetail = async (reference) => {
  return await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/fraud/pending/${reference}`);
};

/**
 * POST /api/v1/admin/fraud/pending/{reference}/approve?comment={comment}
 * Approve decision
 */
export const approveFraudAction = async (reference, comment) => {
  const query = buildQuery({ comment });
  return await httpPost(`${customBaseUrl.tranv2}/api/v1/admin/fraud/pending/${reference}/approve${query}`, {});
};

/**
 * POST /api/v1/admin/fraud/pending/{reference}/reject?comment={comment}
 * Reject decision
 */
export const rejectFraudAction = async (reference, comment) => {
  const query = buildQuery({ comment });
  return await httpPost(`${customBaseUrl.tranv2}/api/v1/admin/fraud/pending/${reference}/reject${query}`, {});
};

/**
 * GET /api/v1/admin/fraud/decisions
 * List fraud decisions (paginated)
 * Query params: page, size
 */
export const getFraudDecisions = async (params = {}) => {
  const query = buildQuery(params);
  return await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/fraud/decisions${query}`);
};

/**
 * GET /api/v1/admin/fraud/decisions/{id}
 * Fetch a single fraud decision by id
 */
export const getFraudDecisionDetail = async (id) => {
  return await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/fraud/decisions/${id}`);
};

/**
 * GET /api/v1/admin/fraud/decisions/summary
 * Aggregated counts by action and reason code
 */
export const getFraudDecisionsSummary = async () => {
  return await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/fraud/decisions/summary`);
};

// ────────────────────────────────────────────────────────────────────────────
// Held Transactions Service
// ────────────────────────────────────────────────────────────────────────────

/**
 * GET /api/v1/admin/held-transactions
 * List held transactions (optionally filter by status / userId, page, size)
 */
export const getHeldTransactions = async (params = {}) => {
  const query = buildQuery(params);
  return await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/held-transactions${query}`);
};

/**
 * GET /api/v1/admin/held-transactions/{reference}
 * Fetch a single held transaction by reference
 */
export const getHeldTransactionDetail = async (reference) => {
  return await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/held-transactions/${reference}`);
};

/**
 * POST /api/v1/admin/held-transactions/{reference}/maker/approve?comment={comment}
 * Maker (SUPER_ADMIN) proposes APPROVE — moves to PENDING_CHECKER for owner
 */
export const proposeMakerApprove = async (reference, comment) => {
  const query = buildQuery({ comment });
  return await httpPost(`${customBaseUrl.tranv2}/api/v1/admin/held-transactions/${reference}/maker/approve${query}`, {});
};

/**
 * POST /api/v1/admin/held-transactions/{reference}/maker/reject?comment={comment}
 * Maker (SUPER_ADMIN) proposes REJECT — moves to PENDING_CHECKER for owner
 */
export const proposeMakerReject = async (reference, comment) => {
  const query = buildQuery({ comment });
  return await httpPost(`${customBaseUrl.tranv2}/api/v1/admin/held-transactions/${reference}/maker/reject${query}`, {});
};

/**
 * POST /api/v1/admin/held-transactions/{reference}/checker/approve?comment={comment}
 * Checker (OWNER) final APPROVE — executes the transaction. Requires a prior maker proposal.
 */
export const executeCheckerApprove = async (reference, comment) => {
  const query = buildQuery({ comment });
  return await httpPost(`${customBaseUrl.tranv2}/api/v1/admin/held-transactions/${reference}/checker/approve${query}`, {});
};

/**
 * POST /api/v1/admin/held-transactions/{reference}/checker/reject?comment={comment}
 * Checker (OWNER) final REJECT — drops the transaction. Allowed from either PENDING state (veto).
 */
export const executeCheckerReject = async (reference, comment) => {
  const query = buildQuery({ comment });
  return await httpPost(`${customBaseUrl.tranv2}/api/v1/admin/held-transactions/${reference}/checker/reject${query}`, {});
};

